import React, { useCallback, useEffect, useState } from "react";
import { StyleSheet } from "react-native";

import { Box, ScrollView, Text, Spinner } from "../../../../../../mads";
import { Button } from "../../../../../../mads/Button";
import { RadioSelectorList } from "../../../../../../mads/RadioSelectList/RadioSelectorList";
import { TextArea } from "../../../../../../mads/TextArea";
import { useI18n } from "../../../../../../shared/I18n";
import {
  type DeclineSurveyReason,
  type DeclineSurveyResponse,
  type StatefulReason,
  Status,
} from "./types";

export interface DeclineSurveyProps {
  /**
   * Callback function on form submit
   */
  onSubmit?: (response: DeclineSurveyResponse) => void;
  /**
   * Collection of reasons
   */
  reasons: DeclineSurveyReason[];
  /**
   * Decline Survey Page Status
   */
  status?: Status;
  /**
   * Bottom safe area inset for proper spacing
   */
  bottomInset?: number;
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#fff",
    maxWidth: 512,
    minWidth: 320,
    marginHorizontal: "auto",
    paddingTop: 0,
    paddingHorizontal: 24,
  },
  header: {
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#333",
  },
  headerSubtitle: {
    fontSize: 14,
    color: "#666",
    marginTop: 8,
  },
  reasonsContainer: {
    marginBottom: 0,
  },
  commentContainer: {
    marginTop: 0,
    marginBottom: 8,
  },
  buttonContainer: {
    marginBottom: 8,
  },
  loadingContainer: {
    padding: 40,
    alignItems: "center",
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: "#666",
  },
  errorContainer: {
    padding: 16,
    backgroundColor: "#ffebee",
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    fontSize: 14,
    color: "#d32f2f",
    textAlign: "center",
  },
});

export function DeclineSurvey({
  onSubmit,
  reasons,
  status = Status.Fetching,
  bottomInset = 0,
}: DeclineSurveyProps): React.JSX.Element {
  const i18n = useI18n();

  const statefulReasons: StatefulReason[] = reasons?.map((reason) => ({
    checked: false,
    id: reason.id,
    description: reason.description,
  }));

  const [items, setItems] = useState<StatefulReason[]>(statefulReasons);
  const [comments, setComments] = useState<string>("");
  const hasSelected = items && items.filter((r) => r.checked).length > 0;

  useEffect(() => {
    if (statefulReasons && items && statefulReasons.length !== items.length) {
      setItems(statefulReasons);
    }
  }, [statefulReasons, items]);

  const handleOnChange = useCallback(
    (selectedIndex: number) => {
      setItems(
        items.map((item, index) => ({
          ...item,
          checked: index === selectedIndex,
        })),
      );
    },
    [items],
  );

  const handleOnSubmit = useCallback(() => {
    const checkedItem = items.find((r) => r.checked);
    if (!checkedItem) {
      return;
    }

    const response: DeclineSurveyResponse = {
      reasonId: checkedItem.id,
      comment: comments.trim() || undefined,
    };

    if (onSubmit) {
      onSubmit(response);
    }
  }, [items, comments, onSubmit]);

  const radioSelectorProps = items.map((reason) => ({
    labelText: reason.description,
    onSelect: () => {},
  }));

  const selectedIndex = items.findIndex((item) => item.checked);

  return (
    <ScrollView contentContainerSx={{ paddingBottom: Math.max(bottomInset ?? 0, 16) + 16 }}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Text sx={styles.headerTitle}>{i18n.gettext("Thank you for your response")}</Text>
          <Text sx={styles.headerSubtitle}>{i18n.gettext("We will notify the recruiter.")}</Text>
        </Box>

        {status === Status.Fetching && (
          <Box sx={styles.loadingContainer}>
            <Spinner />
            <Text sx={styles.loadingText}>{i18n.gettext("Loading...")}</Text>
          </Box>
        )}

        {status !== Status.Fetching && (
          <>
            <Box sx={styles.reasonsContainer}>
              <RadioSelectorList
                labelText={i18n.gettext("Primary reason for decline")}
                explainerText={i18n.gettext(
                  "Providing a reason for your response may help the recruiter fill this job.",
                )}
                radioSelectorProps={radioSelectorProps}
                selectedIndex={selectedIndex >= 0 ? selectedIndex : undefined}
                onSelect={handleOnChange}
                required={false}
              />
            </Box>

            <Box sx={styles.commentContainer}>
              <TextArea
                label={i18n.gettext("Comments")}
                value={comments}
                onInput={setComments}
                ariaText={i18n.gettext("Your comment here")}
                placeholderText={i18n.gettext("Your comment here")}
                textAreaHeight={90}
              />
            </Box>

            {status === Status.Error && (
              <Box sx={styles.errorContainer}>
                <Text sx={styles.errorText}>
                  {i18n.gettext(
                    "An error occurred while submitting your feedback. Please try again later.",
                  )}
                </Text>
              </Box>
            )}

            <Box sx={styles.buttonContainer}>
              <Button
                variant="primary"
                onPress={handleOnSubmit}
                disabled={!hasSelected || status === Status.Submitting}
                loading={status === Status.Submitting}
                size="lg"
              >
                {i18n.gettext("Submit")}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </ScrollView>
  );
}
